import styled from "@emotion/styled";
import { Button } from "@radix-ui/themes";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { BaseTableProps } from "./types";

export const Table = <T extends object>({
  data,
  columns,
  onLoadMore,
  showLoadMore = false,
  loadMoreLabel = "Load More",
  isLoadMoreDisabled = false,
}: BaseTableProps<T>) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <TableContainer>
      <StyledTable>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr key={row.id}>
              {row.getVisibleCells().map(cell => (
                <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
              ))}
            </tr>
          ))}
        </tbody>

        {showLoadMore && onLoadMore && (
          <tfoot>
            <tr>
              <td colSpan={columns.length}>
                <LoadMoreContainer>
                  <Button
                    variant="ghost"
                    size="1"
                    onClick={onLoadMore}
                    disabled={isLoadMoreDisabled}
                  >
                    {loadMoreLabel}
                  </Button>
                </LoadMoreContainer>
              </td>
            </tr>
          </tfoot>
        )}
      </StyledTable>
    </TableContainer>
  );
};

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  font-size: 12px;
`;

const StyledTable = styled.table`
  border-collapse: collapse;
  background-color: var(--gray-0);
  border: 1px solid var(--gray-4);
  overflow: hidden;

  & thead {
    background-color: var(--gray-1);
    font-weight: var(--font-weight-medium);
  }

  & th,
  & td {
    text-align: left;
    padding: 0.5rem;
    border-bottom: 1px solid var(--gray-4);
    &:not(:last-child) {
      padding-right: 1rem;
    }
  }
`;

const LoadMoreContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
`;
